import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { aalignAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { 
  RefreshCw, 
  AlertCircle,
  Megaphone,
  Zap,
  Target,
  DollarSign,
  Calendar,
  Globe,
  Building2,
  Users,
  Briefcase,
  EyeOff,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

function AalignCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(function() {
    fetchCampaigns();
  }, []);

  function fetchCampaigns() {
    setError(null);
    aalignAPI.getCampaignsDetailed().then(function(response) {
      if (response.data.success) {
        setCampaigns(response.data.data || []);
      } else {
        setError(response.data.error || 'Failed to fetch campaigns');
      }
    }).catch(function(err) {
      console.error('Failed to fetch AALIGN campaigns:', err);
      setError('Failed to connect to AALIGN. Please try again later.');
    }).finally(function() {
      setLoading(false);
    });
  }

  function handleRefresh() {
    setRefreshing(true);
    setError(null);
    aalignAPI.getCampaignsDetailed().then(function(response) {
      if (response.data.success) {
        setCampaigns(response.data.data || []);
        toast.success('Campaigns refreshed');
      } else {
        setError(response.data.error || 'Failed to fetch campaigns');
      }
    }).catch(function(err) {
      setError('Failed to connect to AALIGN');
    }).finally(function() {
      setRefreshing(false);
    });
  }

  function maskCampaignName(name) {
    if (!name) return '••••••••';
    const words = name.replace(/_/g, ' ').split(' ');
    if (words.length <= 2) {
      const first = words[0].substring(0, 3) + '•••';
      const second = words[1] ? ' ' + words[1].substring(0, 2) + '•••' : '';
      return first + second;
    }
    return words[0] + ' ' + words[1].substring(0, 2) + '••• •••';
  }

  function formatBudget(budget) {
    if (!budget) return 'N/A';
    const num = parseFloat(budget);
    if (isNaN(num)) return budget;
    return '$' + num.toLocaleString();
  }

  function renderGeoTags(geoList) {
    const items = [];
    const max = Math.min(geoList.length, 3);
    for (let i = 0; i < max; i++) {
      items.push(
        <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
          {geoList[i]}
        </span>
      );
    }
    if (geoList.length > 3) {
      items.push(
        <span key="more" className="text-xs text-slate-400">+{geoList.length - 3} more</span>
      );
    }
    return items;
  }

  function renderIndustryTags(industries) {
    const items = [];
    const max = Math.min(industries.length, 2);
    for (let i = 0; i < max; i++) {
      items.push(
        <span key={i} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded truncate max-w-[150px]">
          {industries[i]}
        </span>
      );
    }
    if (industries.length > 2) {
      items.push(
        <span key="more" className="text-xs text-slate-400">+{industries.length - 2} more</span>
      );
    }
    return items;
  }

  function renderPricingTags(pricing) {
    const items = [];
    for (let i = 0; i < pricing.length; i++) {
      const p = pricing[i];
      items.push(
        <div key={i} className="bg-emerald-50 rounded px-2 py-1">
          <span className="text-xs font-medium text-emerald-700">{p.lead_type}</span>
          <span className="text-xs text-emerald-600 ml-1">${p.price}/lead</span>
          <span className="text-xs text-slate-400 ml-1">({p.target} target)</span>
        </div>
      );
    }
    return items;
  }

  function formatCampaignId(id) {
    if (!id || id.length < 6) return id || 'N/A';
    return id.substring(0, 2) + 'xxx' + id.slice(-4);
  }

  function renderCampaignCard(campaign, index) {
    const campaignName = campaign.campaign_name || 'Campaign';
    const maskedName = maskCampaignName(campaignName);
    const specs = campaign.specifications || {};
    const geoList = specs.geo || [];
    const industries = specs.industry_types || [];
    const jobTitles = specs.job_title_keywords || '';
    const pricing = campaign.client_pricing || [];
    const campaignId = campaign.id || '';
    const statusClass = campaign.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-600';
    
    return (
      <Card 
        key={campaignId || index}
        className="hover:shadow-md transition-shadow border-l-4 border-l-sky-500"
        data-testid={'campaign-card-' + index}
      >
        <CardContent className="p-0">
          <div className="p-4 pb-3 border-b bg-gradient-to-r from-sky-50 to-white">
            <div className="flex items-start justify-between gap-2 mb-2">
              <Badge className="bg-sky-100 text-sky-700 border-sky-200">
                <Zap className="w-3 h-3 mr-1" />
                AALIGN Campaign
              </Badge>
              <Badge variant="outline" className={statusClass}>
                {campaign.status || 'Unknown'}
              </Badge>
            </div>
            <h3 className="font-semibold text-slate-400 text-lg flex items-center gap-2">
              <EyeOff className="w-4 h-4" />
              {maskedName}
            </h3>
            <p className="text-xs text-slate-400 mt-1 font-mono">CID: {formatCampaignId(campaignId)}</p>
          </div>

          <div className="p-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              {campaign.type && (
                <span className="inline-flex items-center text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                  <Briefcase className="w-3 h-3 mr-1" />
                  {campaign.type}
                </span>
              )}
              {campaign.start_date && campaign.end_date && (
                <span className="inline-flex items-center text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                  <Calendar className="w-3 h-3 mr-1" />
                  {campaign.start_date} - {campaign.end_date}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="flex items-center text-xs text-slate-500 mb-1">
                  <Target className="w-3 h-3 mr-1" />
                  Total Target
                </div>
                <div className="font-semibold text-slate-900">{campaign.total_target || 'N/A'} leads</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <div className="flex items-center text-xs text-slate-500 mb-1">
                  <DollarSign className="w-3 h-3 mr-1" />
                  Total Budget
                </div>
                <div className="font-semibold text-slate-900">{formatBudget(campaign.total_budget)}</div>
              </div>
            </div>

            {geoList.length > 0 && (
              <div>
                <div className="flex items-center text-xs text-slate-500 mb-1">
                  <Globe className="w-3 h-3 mr-1" />
                  Regions
                </div>
                <div className="flex flex-wrap gap-1">{renderGeoTags(geoList)}</div>
              </div>
            )}

            {industries.length > 0 && (
              <div>
                <div className="flex items-center text-xs text-slate-500 mb-1">
                  <Building2 className="w-3 h-3 mr-1" />
                  Industries
                </div>
                <div className="flex flex-wrap gap-1">{renderIndustryTags(industries)}</div>
              </div>
            )}

            {jobTitles && (
              <div>
                <div className="flex items-center text-xs text-slate-500 mb-1">
                  <Users className="w-3 h-3 mr-1" />
                  Target Roles
                </div>
                <p className="text-xs text-slate-600 line-clamp-2">{jobTitles}</p>
              </div>
            )}

            {pricing.length > 0 && (
              <div className="border-t pt-3 mt-3">
                <div className="text-xs text-slate-500 mb-2">Lead Types & Pricing</div>
                <div className="flex flex-wrap gap-2">{renderPricingTags(pricing)}</div>
              </div>
            )}
          </div>

          <div className="px-4 py-3 bg-slate-50 border-t flex items-center justify-between">
            <div className="text-xs text-slate-500">Apply to fulfill this campaign</div>
            <Button size="sm" variant="outline" className="text-sky-600 border-sky-200 hover:bg-sky-50" disabled>
              Coming Soon
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderCampaignCards() {
    const items = [];
    for (let i = 0; i < campaigns.length; i++) {
      items.push(renderCampaignCard(campaigns[i], i));
    }
    return items;
  }

  function renderSkeletons() {
    const items = [];
    for (let i = 0; i < 3; i++) {
      items.push(
        <Skeleton key={i} className="h-80" />
      );
    }
    return items;
  }

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-5 w-96 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {renderSkeletons()}
          </div>
        </div>
      </Layout>
    );
  }

  const activeCount = campaigns.filter(function(c) { return c.status === 'Active'; }).length;

  return (
    <Layout>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="aalign-campaigns-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">AALIGN Campaigns</h1>
            </div>
            <p className="text-slate-600">
              Browse buyer campaigns from AALIGN platform and apply to fulfill their lead requirements
            </p>
          </div>
          <Button 
            onClick={handleRefresh} 
            disabled={refreshing}
            variant="outline"
            data-testid="refresh-campaigns-btn"
          >
            <RefreshCw className={'w-4 h-4 mr-2' + (refreshing ? ' animate-spin' : '')} />
            Refresh
          </Button>
        </div>

        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <div>
                  <div className="font-medium text-red-900">Connection Error</div>
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border-sky-100">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-sky-700">{campaigns.length}</div>
              <div className="text-sm text-sky-600">Total Campaigns</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-emerald-700">{activeCount}</div>
              <div className="text-sm text-emerald-600">Active</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-100">
            <CardContent className="p-4">
              <div className="text-3xl font-bold text-purple-700">AALIGN</div>
              <div className="text-sm text-purple-600">Platform Source</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-slate-600" />
              Browse Campaigns
            </CardTitle>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Megaphone className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">No campaigns found</h3>
                <p className="text-slate-500 mb-4">
                  There are currently no buyer campaigns available from AALIGN
                </p>
                <Button onClick={handleRefresh} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {renderCampaignCards()}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6 border-sky-200 bg-sky-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-sky-600" />
              </div>
              <div>
                <div className="font-medium text-sky-900">How it works</div>
                <div className="text-sm text-sky-700">
                  These campaigns are created by buyers on the AALIGN platform. As a seller, you can 
                  view campaign requirements and apply to fulfill leads. Campaign names are partially 
                  hidden for confidentiality until you are approved.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default AalignCampaigns;
